'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "start", "end", "addPropertiesToDroppedEvent"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from "../../base-ui-copy/utils/useRenderElement.mjs";
import { useCompositeListItem } from "../../base-ui-copy/composite/list/useCompositeListItem.mjs";
import { useCompositeListContext } from "../../base-ui-copy/composite/list/CompositeListContext.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { schedulerNowSelectors } from "../../scheduler-selectors/index.mjs";
import { EVENT_CREATION_PRECISION_MINUTE } from "../../constants/index.mjs";
import { useEventCreation } from "../../internals/utils/useEventCreation.mjs";
import { useKeyboardEventCreation } from "../../internals/utils/useKeyboardEventCreation.mjs";
import { getNavigationTarget } from "../../internals/utils/getNavigationTarget.mjs";
import { useCalendarGridRootContext } from "../root/CalendarGridRootContext.mjs";
import { CalendarGridTimeColumnContext } from "./CalendarGridTimeColumnContext.mjs";
import { useTimeDropTarget } from "./useTimeDropTarget.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
export const CalendarGridTimeColumn = /*#__PURE__*/React.forwardRef(function CalendarGridTimeColumn(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      start,
      end,
      addPropertiesToDroppedEvent
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const {
    focusedCell,
    setFocusedCell,
    rowTypes,
    rowsPerType
  } = useCalendarGridRootContext();
  const isCurrentDay = useStore(store, schedulerNowSelectors.isCurrentDay, start);
  const {
    ref: listItemRef,
    index
  } = useCompositeListItem();
  const {
    elementsRef
  } = useCompositeListContext();
  const cellRef = React.useRef(null);
  const hasFocus = focusedCell?.rowType === 'time-grid' && focusedCell?.rowIndex === 0 && focusedCell?.columnIndex === index;
  const {
    getCursorPositionInElementMs,
    ref: dropTargetRef
  } = useTimeDropTarget({
    start,
    end,
    addPropertiesToDroppedEvent
  });
  const eventCreationProps = useEventCreation(({
    event,
    creationConfig
  }) => {
    const offsetMs = getCursorPositionInElementMs({
      input: {
        clientY: event.clientY
      },
      elementRef: dropTargetRef
    });
    const anchor = adapter.addMilliseconds(start, offsetMs);
    const startDate = adapter.addMinutes(anchor, -(adapter.getMinutes(anchor) % EVENT_CREATION_PRECISION_MINUTE));
    return {
      surfaceType: 'time-grid',
      start: startDate,
      end: adapter.addMinutes(startDate, creationConfig.duration),
      resourceId: null
    };
  });
  const triggerKeyboardCreation = useKeyboardEventCreation(({
    creationConfig
  }) => {
    const noon = adapter.setHours(adapter.setMinutes(start, 0), 12);
    return {
      surfaceType: 'time-grid',
      start: noon,
      end: adapter.addMinutes(noon, creationConfig.duration),
      resourceId: null
    };
  });

  // Apply DOM focus when this cell becomes the focused cell
  React.useEffect(() => {
    if (hasFocus && cellRef.current && !cellRef.current.contains(document.activeElement)) {
      cellRef.current.focus({
        preventScroll: true
      });
    }
  }, [hasFocus]);
  const handleKeyDown = event => {
    const target = getNavigationTarget(event.key, 'time-grid', 0, index, {
      columnCount: elementsRef.current.length,
      rowTypes,
      rowsPerType
    });
    if (target) {
      event.preventDefault();
      setFocusedCell(target);
      return;
    }
    if (event.key === 'Enter' && event.target === event.currentTarget && triggerKeyboardCreation) {
      event.preventDefault();
      triggerKeyboardCreation();
    }
  };
  const handleFocus = event => {
    if (event.target === event.currentTarget) {
      setFocusedCell({
        rowType: 'time-grid',
        rowIndex: 0,
        columnIndex: index
      });
    }
  };
  const state = React.useMemo(() => ({
    current: isCurrentDay
  }), [isCurrentDay]);
  const contextValue = React.useMemo(() => ({
    start,
    end,
    index,
    hasFocus,
    getCursorPositionInElementMs
  }), [start, end, index, hasFocus, getCursorPositionInElementMs]);
  const keyboardProps = {
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus
  };
  const element = useRenderElement('div', componentProps, {
    state,
    ref: [forwardedRef, dropTargetRef, listItemRef, cellRef],
    props: [{
      role: 'gridcell',
      'aria-colindex': index + 1
    }, keyboardProps, eventCreationProps, elementProps]
  });
  return /*#__PURE__*/_jsx(CalendarGridTimeColumnContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") CalendarGridTimeColumn.displayName = "CalendarGridTimeColumn";